import { useState, useMemo, useEffect } from "react";
import { H3, Label, Anchor } from "../Text";
import { toast } from "react-toastify";
import Circle from "../Animation/Circle";
import { Button } from "../Buttons";
import { Request } from "../../hooks/Request";
import TableComplete from "../Tables/TableComplete";
import { requestAuth } from "../services/RequestService";
import { useDialog } from "../../contexts/DialogContext";
import { DialogConfirmation } from "../Modal";

/**
 * Tipo: subDataDynamicAdd — Lista dinámica manual
 *
 * Schema en field.format / field.Format (JSON).
 * SubData slots (máx 4): Label, Description, Observation, Description2
 *
 * Archivo: POST /files (FormData) → se guarda la URL en el slot.
 */

const BACKEND_KEYS = ["Label", "Description", "Observation", "Description2"];
const BACKEND_READ = ["label", "description", "observation", "description2"];

const DEFAULT_SCHEMA = {
  fields: [
    { label: "Campo 1", type: "text" },
    { label: "Campo 2", type: "text" },
  ],
};

function parseSchema(format) {
  const raw =
    typeof format === "string"
      ? format
      : format && typeof format === "object"
      ? JSON.stringify(format)
      : "";

  if (!raw || !String(raw).trim()) {
    return DEFAULT_SCHEMA.fields.slice(0, 4);
  }

  try {
    const parsed = typeof format === "object" ? format : JSON.parse(raw);
    const list = Array.isArray(parsed?.fields)
      ? parsed.fields
      : Array.isArray(parsed)
      ? parsed
      : null;
    if (!list || !list.length) return DEFAULT_SCHEMA.fields.slice(0, 4);
    return list.slice(0, 4).map((f) => ({
      label: f.label || f.name || "Campo",
      type: (f.type || "text").toLowerCase(),
      dropdownUrl: f.dropdownUrl || f.url || "",
      options: Array.isArray(f.options)
        ? f.options.map((o) =>
            typeof o === "string"
              ? { value: o, label: o }
              : {
                  value: o.value ?? o.name ?? "",
                  label: o.label ?? o.name ?? o.value ?? "",
                }
          )
        : [],
    }));
  } catch {
    return String(raw)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 4)
      .map((label) => ({ label, type: "text", options: [], dropdownUrl: "" }));
  }
}

function isFileUrl(value) {
  if (!value || typeof value !== "string") return false;
  return (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("/") ||
    value.includes("cloudinary") ||
    value.includes("/files/")
  );
}

/** Mismo flujo que ContainDocuments / SecondaryForm / ControlField */
async function uploadFileLikeSystem(file, procedureId, fieldName) {
  if (!procedureId) {
    throw new Error("No se encontró el ID del trámite (procedureId)");
  }
  const formData = new FormData();
  formData.append("Name", fieldName || "archivo");
  formData.append("File", file);
  formData.append("originalName", file.name || "archivo");
  formData.append("Status", "verified");
  formData.append("ProcedureId", String(procedureId));
  const res = await requestAuth("post", "/files", formData);
  const url = res?.data?.url ?? res?.url ?? res?.data?.Url;
  if (!url) {
    throw new Error(
      "El servidor no devolvió la URL del archivo. Revisa Cloudinary / POST /files"
    );
  }
  return url;
}

const InputSubDataDynamicAdd = ({ field }) => {
  const { openDialog, dialogClose } = useDialog();

  const procedureId = field?.id;
  const fieldId = field?.fieldId ?? field?.FieldId;

  const schema = useMemo(
    () => parseSchema(field?.format ?? field?.Format ?? ""),
    [field?.format, field?.Format]
  );

  const listUrl =
    procedureId && fieldId
      ? `/dataSet/procedure/${procedureId}/field/${fieldId}/subdata`
      : null;

  const { data, loading, call } = Request({
    urlApi: listUrl || "/dataSet/procedure/0/field/0/subdata",
  });

  function handleForm(item) {
    openDialog(
      <>
        <FormDynamic
          item={item}
          reload={call}
          field={field}
          schema={schema}
          onClose={dialogClose}
        />
        <br />
      </>
    );
  }

  function handleDelete(row) {
    openDialog(
      <DialogConfirmation
        children={<H3>¿Estas seguro que quieres eliminar este item?</H3>}
        onClose={dialogClose}
        method="delete"
        url={`/dataSet/procedure/subData/${row.id}`}
        texBtn="Eliminar"
        handleSuccess={call}
      />
    );
  }

  const tableHeader = [
    ...schema.map((col, idx) => ({
      name: BACKEND_READ[idx],
      label: col.label,
      type: "custom",
      builder: (value) => {
        // Archivo o cualquier URL larga → enlace corto (evita barra de scroll "congelada")
        if (isFileUrl(value)) {
          return (
            <Anchor
              className="text-[#1d4ed8] underline break-all"
              href={value}
              target="_blank"
              rel="noopener noreferrer"
            >
              Ver archivo
            </Anchor>
          );
        }
        if (value == null || value === "") return "-";
        const str = String(value);
        // Textos muy largos: truncar en tabla
        if (str.length > 40) {
          return (
            <span title={str} className="block max-w-[180px] truncate">
              {str}
            </span>
          );
        }
        return str;
      },
    })),
    {
      name: "add",
      type: "action",
      label: "Opciones",
      sticky: true,
      actions: [
        {
          label: "Editar",
          icon: "fas fa-edit",
          action: handleForm,
          color: "text-[#1d4ed8]",
        },
        {
          label: "Eliminar",
          icon: "fas fa-trash",
          action: handleDelete,
          color: "text-red-500",
        },
      ],
    },
  ];

  if (!procedureId || !fieldId) {
    return (
      <div className="bg-red-100 text-red-700 p-3 rounded-md text-sm">
        Error: no se pudo obtener procedureId o fieldId del campo &quot;
        {field?.label}&quot;. Revisa que el formulario pase id y fieldId.
      </div>
    );
  }

  return (
    <div className="bg-gray-300/40 p-2 rounded-md">
      <Label htmlFor={field.name} className={`${field.className} my-0`}>
        {field.labelCustom ? field.labelCustom : field.label}
      </Label>

      <div className="bg-gray-100 rounded-lg p-3 pt-2">
        <FormDynamic
          reload={call}
          field={field}
          schema={schema}
          onClose={() => {}}
        />
      </div>
      <br />

      {!loading ? (
        <Circle />
      ) : (
        <div className="w-full max-w-full overflow-x-auto">
          <TableComplete
            header={tableHeader}
            data={Array.isArray(data) ? data : data?.items || data?.data || []}
          />
        </div>
      )}
    </div>
  );
};

const FormDynamic = (props) => {
  const { item = null, reload, field, schema, onClose } = props;
  const [saving, setSaving] = useState(false);
  // Cambia tras guardar → remonta el <input type="file"> y limpia el nombre visible
  const [fileInputKey, setFileInputKey] = useState(0);

  const procedureId = field?.id;
  const fieldId = field?.fieldId ?? field?.FieldId;

  const initial = {};
  schema.forEach((col, idx) => {
    const key = BACKEND_KEYS[idx];
    const readKey = BACKEND_READ[idx];
    initial[key] = item?.[readKey] ?? item?.[key] ?? "";
  });
  const [values, setValues] = useState(initial);

  async function handleSubmit(e) {
    if (e) {
      if (e.preventDefault) e.preventDefault();
      if (e.stopPropagation) e.stopPropagation();
      if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
        e.nativeEvent.stopImmediatePropagation();
      }
    }

    if (!procedureId || !fieldId) {
      toast.error("Falta ID de trámite o de campo. No se puede guardar.");
      return;
    }

    const firstKey = BACKEND_KEYS[0];
    const firstVal = values[firstKey];
    const firstEmpty =
      firstVal === "" ||
      firstVal === null ||
      firstVal === undefined ||
      (typeof firstVal === "string" && !firstVal.trim());

    if (firstEmpty && !(firstVal instanceof File)) {
      toast.error(
        `El campo "${schema[0]?.label || "primero"}" es obligatorio`
      );
      return;
    }

    setSaving(true);
    try {
      const payload = {
        Label: "",
        Description: "",
        Observation: "",
        Description2: "",
        NumberValue: 0,
      };

      for (let i = 0; i < schema.length; i++) {
        const key = BACKEND_KEYS[i];
        const col = schema[i];
        let val = values[key];

        if (col.type === "file" && val instanceof File) {
          try {
            val = await uploadFileLikeSystem(
              val,
              procedureId,
              col.label || field.label || field.name || "archivo"
            );
          } catch (err) {
            console.error("Error subiendo archivo:", err);
            const msg =
              err?.response?.data?.message ||
              err?.response?.data ||
              err?.message ||
              "Error al subir el archivo";
            toast.error(
              typeof msg === "string"
                ? msg
                : "Se produjo un error al subir el archivo"
            );
            setSaving(false);
            return;
          }
        }

        if (val instanceof File) {
          toast.error("No se pudo procesar el archivo");
          setSaving(false);
          return;
        }

        // URLs o texto
        payload[key] = val != null && val !== undefined ? String(val) : "";
      }

      const url = item
        ? `/dataSet/procedure/subData/${item.id}`
        : `/dataSet/procedure/${procedureId}/field/${fieldId}/subdata`;

      console.log("[Lista dinámica] Guardando:", {
        method: item ? "put" : "post",
        url,
        payload,
      });

      await requestAuth(item ? "put" : "post", url, payload);

      // UX más rápida: liberar UI al instante (no esperar el refresh de la lista)
      const reset = {};
      schema.forEach((_, idx) => {
        reset[BACKEND_KEYS[idx]] = "";
      });
      setValues(reset);
      setFileInputKey((k) => k + 1); // limpia nombre del archivo en el input
      setSaving(false);
      toast.success("Registro exitoso");
      if (typeof onClose === "function") {
        onClose();
      }

      // Refrescar tabla en segundo plano (no bloquea el botón)
      if (typeof reload === "function") {
        Promise.resolve(reload()).catch(() => {});
      }
      return;
    } catch (err) {
      console.error("[Lista dinámica] Error al guardar:", err);
      const status = err?.response?.status;
      const body = err?.response?.data;
      const msg =
        (typeof body === "string" && body) ||
        body?.message ||
        body?.title ||
        err?.message ||
        "Se produjo un error al guardar";
      toast.error(
        status ? `(${status}) ${msg}` : typeof msg === "string" ? msg : "Error al guardar"
      );
    } finally {
      setSaving(false);
    }
  }

  function handleChange(e, key) {
    const { value, type, files } = e.target;
    if (type === "file") {
      const file = files && files[0] ? files[0] : null;
      setValues((prev) => ({ ...prev, [key]: file }));
      return;
    }
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  // IMPORTANTE: NO usar <form> aquí.
  // Este bloque vive DENTRO del FormDefault del trámite.
  // Un <form> anidado hace que el navegador envíe el form padre y "recargue".
  return (
    <div
      className="flex flex-col gap-2"
      onKeyDown={(e) => {
        // Evitar que Enter envíe el formulario padre del trámite
        if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(e);
        }
      }}
    >
      {schema.map((col, idx) => {
        const key = BACKEND_KEYS[idx];
        const val = values[key];

        return (
          <div key={idx}>
            <Label className="mt-2">{col.label}</Label>
            <div className={idx === schema.length - 1 ? "flex gap-2 items-start" : ""}>
              <div className="flex-1">
                <DynamicFieldInput
                  col={col}
                  value={val}
                  name={key}
                  onChange={(e) => handleChange(e, key)}
                  fileInputKey={fileInputKey}
                />
              </div>
              {idx === schema.length - 1 ? (
                <Button
                  type="button"
                  typeBtn="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSubmit(e);
                  }}
                  className="px-3 py-2 whitespace-nowrap"
                  disabled={saving}
                >
                  {saving ? "..." : "Guardar"}
                </Button>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/** UI de archivo igual al tipo Archivo del sistema (InputFiles) */
function FileInputLikeSystem({ value, name, onChange, inputKey = 0 }) {
  const hasUrl = typeof value === "string" && isFileUrl(value);
  const hasFile = value instanceof File;

  return (
    <>
      {hasUrl ? (
        <div className="mb-1 text-sm">
          Archivo subido:{" "}
          <Anchor
            className="text-[#1d4ed8] underline"
            href={value}
            target="_blank"
            rel="noopener noreferrer"
          >
            Ver archivo
          </Anchor>
        </div>
      ) : null}
      {hasFile ? (
        <div className="mb-1 text-sm text-gray-700">
          Archivo seleccionado: {value.name}
        </div>
      ) : null}
      <div className="relative">
        <input
          key={inputKey}
          className="w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#1d4ed8] file:text-white hover:file:bg-[#1e40af]"
          type="file"
          name={name}
          onChange={onChange}
        />
      </div>
    </>
  );
}

function DynamicFieldInput({ col, value, name, onChange, fileInputKey = 0 }) {
  const commonClass = "w-full p-2 rounded-md";

  if (col.type === "file") {
    return (
      <FileInputLikeSystem
        key={fileInputKey}
        value={value}
        name={name}
        onChange={onChange}
        inputKey={fileInputKey}
      />
    );
  }

  if (col.type === "textarea") {
    return (
      <textarea
        value={value || ""}
        onChange={onChange}
        className={commonClass}
        name={name}
        rows={3}
        placeholder={col.label}
      />
    );
  }

  if (col.type === "select") {
    if (col.dropdownUrl) {
      return (
        <SelectFromDropdown
          url={col.dropdownUrl}
          value={value || ""}
          name={name}
          onChange={onChange}
          className={commonClass}
        />
      );
    }
    return (
      <select
        value={value || ""}
        onChange={onChange}
        className={commonClass}
        name={name}
      >
        <option value="">Seleccione...</option>
        {(col.options || []).map((opt, i) => (
          <option key={i} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  }

  const inputType =
    col.type === "date" ? "date" : col.type === "number" ? "number" : "text";

  return (
    <input
      value={value || ""}
      onChange={onChange}
      className={commonClass}
      type={inputType}
      name={name}
      placeholder={col.label}
    />
  );
}

function SelectFromDropdown({ url, value, name, onChange, className }) {
  const path = url.startsWith("/") ? url : `/Dropdown/${url}/options`;
  const { data, loading } = Request({ urlApi: path });
  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (!loading || !data) return;
    const list = Array.isArray(data) ? data : data?.data || data?.items || [];
    setOptions(
      list.map((o) => ({
        value: o.name ?? o.value ?? o.label ?? String(o),
        label: o.name ?? o.label ?? o.value ?? String(o),
      }))
    );
  }, [data, loading]);

  return (
    <select
      value={value}
      onChange={onChange}
      className={className}
      name={name}
      disabled={!loading && options.length === 0}
    >
      <option value="">{!loading ? "Cargando..." : "Seleccione..."}</option>
      {options.map((opt, i) => (
        <option key={i} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

export default InputSubDataDynamicAdd;